import React, { useEffect, useState } from 'react';
import { useAppSelector } from '../../hooks/storeHooks';
import '../rod-lift-analysis/RodLiftAnalysis/RodLiftAnalysis.css';
import '../../styles/Analysis.css';
import LidarScanMap from '../rod-lift-analysis/LidarScan';
import Loader from '../common/Loader/Loader';
import FullScreenIcon from '../common/FullScreenIcon/FullScreenIcon';
import { Switch } from '@headlessui/react';
import './dynamometer.scss';
import { RGBColor } from '../../models/Card'; // Imported RGBColor
import { ReactComponent as NoData } from '../../images/no-data.svg';

const cardViewOptions = [
  {
    id: 1,
    label: 'Surface',
  },
  {
    id: 2,
    label: 'POC downhole',
  },
  {
    id: 3,
    label: 'XDiag Downhole',
  },
  {
    id: 4,
    label: 'Predicted Surface',
  },
  {
    id: 7,
    label: 'Load limits & setpoints',
  },
  {
    id: 5,
    label: 'Permissible load',
  },
  //   {
  //     id: 9,
  //     label: 'Setpoints',
  //   },
];

// interface UrlParams {
//   assetId: string;
// }

interface SelectedCardTrend {
  id: number;
  label: string;
}

// interface RodLiftAnalysisProps {
//   timelineProps: AnalysisTimelineProps;
// }

const Dynamometer = (props: {
  setMinDate: (date: Date) => void;
  setMaxDate: (date: Date) => void;
  startDate: Date;
  endDate: Date;
  cardDateItems: { date: string; isSelected: boolean; cardTypeName: string; color: RGBColor }[];
  onClick: (index: number, date: string) => void;
  cardTypeNames: string[];
}) => {
  const { setMinDate, setMaxDate, startDate, endDate } = props;
  //   const dispatch = useAppDispatch();
  const cardDateStore = useAppSelector((state) => state.rodLiftAnalysis);
  const cardCoordinatesStore = useAppSelector((state) => state.rodLiftAnalysisCardCoordinates);
  // const enabledStates = useAppSelector((state) => state.viewOptions.enabledStates);
  const isLoadingCards = useAppSelector((state) => state.rodLiftAnalysis.isLoading);
  const isLoadingCoordinates = useAppSelector((state) => state.rodLiftAnalysisCardCoordinates.isLoading);

  //   const { assetId } = useParams<keyof UrlParams>() as UrlParams;
  //   const { pathname } = useLocation();

  const [selectedCardTrends, setSelectedCardTrends] = useState<SelectedCardTrend[]>(
    [
      { id: 1, label: 'Surface' },
      { id: 2, label: 'POC downhole' },
    ],
    // cardViewOptions.map((option) => ({ id: option.id, label: option.label })),
  );
  const [selectedCardTypeNames, setSelectedCardTypeNames] = useState<string[]>([]);

  useEffect(() => {
    if (!isLoadingCards && !isLoadingCoordinates && cardDateStore.values && cardDateStore.values.length > 0) {
      const dates = cardDateStore.values.map((x) => new Date(x.date).getTime());
      const minDate = new Date(Math.min(...dates));
      const maxDate = new Date(Math.max(...dates));

      if (minDate === maxDate) {
        minDate.setDate(minDate.getDate() - 1);
        maxDate.setDate(maxDate.getDate() + 1);
      }

      setMinDate(minDate);
      setMaxDate(maxDate);
    }
  }, [cardDateStore.values.length, isLoadingCards, isLoadingCoordinates]);

  useEffect(() => {
    if (!isLoadingCards && !isLoadingCoordinates && startDate && endDate && cardDateStore.values) {
      const filteredCardDates = cardDateStore.values.filter((x) => {
        const cardDate = new Date(x.date);
        return cardDate >= startDate && cardDate <= endDate;
      });

      setSelectedCardTypeNames(filteredCardDates.map((date) => date.cardTypeName));
    }
  }, [cardDateStore.values, startDate, endDate, isLoadingCards, isLoadingCoordinates]);

  const getSelectedCards = () => {
    if (!cardDateStore.selectedCardDates || !cardDateStore.values) {
      return [];
    }

    const selectedCardDates = cardDateStore.selectedCardDates.filter((cardDate) =>
      startDate && endDate ? new Date(cardDate) >= startDate && new Date(cardDate) <= endDate : true,
    );

    // Use Redux batch tracking - only use coordinates when not in batch operation
    const coordinatesToUse = cardCoordinatesStore.isBatchOperation ? {} : cardCoordinatesStore.values;

    const selectedCards = selectedCardDates
      .map((cardDate) => ({
        cardDate: cardDate,
        cardTrends: coordinatesToUse[cardDate]?.cardTrends,
        color: cardDateStore.values.find((cardDateItem) => cardDateItem.date == cardDate)?.color,
      }))
      .filter((card) => card !== undefined && card.cardTrends !== undefined);
    return selectedCards;
  };

  const [isFullScreen, setIsFullScreen] = useState<boolean>(false);
  const gridRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleFullscreenChange = () =>
      setIsFullScreen(!!document.fullscreenElement && document.fullscreenElement === gridRef.current);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const toggleFullscreen = () => {
    if (!gridRef.current) return;
    if (!document.fullscreenElement) {
      gridRef.current.requestFullscreen().catch((err) => {
        console.error(`Error attempting to enable fullscreen: ${err.message}`);
      });
    } else {
      document.exitFullscreen();
    }
  };

  return (
    <div ref={gridRef} className={`card-viewer ${isFullScreen ? 'card-viewer-full-screen-override' : 'rounded-[8px]'}`}>
      <div className='dynamometer-header flex items-center justify-between'>
        <div className='flex items-center justify-between w-full'>
          <div className='flex items-center'>
            <h2>Dynamometer</h2>
          </div>
          <div className='mr-[28px]'>
            <button onClick={toggleFullscreen} title='Toggle Fullscreen' type='button'>
              <FullScreenIcon />
            </button>
          </div>
        </div>
      </div>
      <div className='card-header-divider' />
      <div className='dynamometer-body'>
        <div className='card-viewer-card'>
          <div className='card-view-options flex flex-wrap'>
            {cardViewOptions.map((option, index) => (
              <div key={index} className='flex mr-4 mb-1 card-toggle'>
                <Switch.Group as='div' className='flex'>
                  <Switch
                    checked={selectedCardTrends.some((trend) => trend.id === option.id)}
                    onChange={(value: boolean) => {
                      console.log('Switch changed:', value, option);
                      setSelectedCardTrends((cards) => {
                        const updatedCards = cards ? [...cards] : [];
                        const trendIndex = updatedCards.findIndex((card) => card.id === option.id);
                        if (trendIndex !== -1) {
                          updatedCards.splice(trendIndex, 1);
                        } else {
                          updatedCards.push({ id: option.id, label: option.label });
                          setSelectedCardTypeNames([...selectedCardTypeNames, option.label]);
                        }
                        return updatedCards;
                      });
                    }}
                    className={`custom-switch ${selectedCardTrends.some((trend) => trend.id === option.id) ? 'checked' : 'unchecked'}`}
                  >
                    <span
                      aria-hidden='true'
                      className={`custom-switch-handle ${selectedCardTrends.some((trend) => trend.id === option.id) ? 'checked' : ''}`}
                    />
                  </Switch>
                </Switch.Group>
                <div className='card-options'>
                  <span className='font-medium'>{option.label}</span>
                </div>
              </div>
            ))}
          </div>
          <div className='flex flex-row flex-grow flex-shrink'>
            {isLoadingCards || isLoadingCoordinates ? (
              <Loader />
            ) : selectedCardTrends.length > 0 ? (
              <LidarScanMap
                selectedCardTrends={selectedCardTrends}
                cards={getSelectedCards()}
                height={isFullScreen ? '700px' : '500px'}
              />
            ) : (
              <div className='flex flex-col items-center justify-center h-full w-full py-20'>
                <NoData />
                <div className='no-data-text mt-2'>Please select a card</div>
              </div>
            )}
            {/* <div className='card-viewer-date-list'>
              <CardDateList cardDateItems={cardDateItems} onClick={onClick} cardTypeNames={cardTypeNames} />
            </div> */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dynamometer;
